/**
 * Standardized metric labels for content display and metrics tables.
 * Use across PublicSponsorLink, GroupPlatformStats, SponsorGroupPDF, ContentStatistics, and related components.
 *
 * Each metric has `long` and `short` variants:
 * - long: for web tables, tooltips, readable content
 * - short: for PDF compact tables, narrow columns
 */

export const METRICS_LABELS = {
  cantidad: { long: "Piezas", short: "Piezas" },
  tipoContenido: { long: "Tipo", short: "Tipo" },
  vistas: { long: "Views", short: "Views" },
  espectadoresUnicos: { long: "Esp. únicos", short: "Esp. únicos" },
  vistasInvolucradas: {
    long: "Engaged Views",
    short: "Engaged Views",
  },
  espectadoresEnVivo: { long: "Esp. en vivo prom.", short: "Esp. en vivo prom." },
  espectadoresEnVivoMencion: {
    long: "Espectadores en vivo (mención)",
    short: "Esp. en vivo",
  },
  espectadoresEnVivoPeak: { long: "Esp. peak", short: "Esp. peak" },
  espectadoresEnVivoPromedio: {
    long: "Esp. en vivo prom.",
    short: "Esp. en vivo prom.",
  },
  likes: { long: "Likes", short: "Likes" },
  comentarios: { long: "Comentarios", short: "Comen." },
  shares: { long: "Shares", short: "Shares" },
  guardados: { long: "Saves", short: "Saves" },
  alcance: { long: "Reach", short: "Reach" },
  interacciones: { long: "Interacciones", short: "Inter." },
  tasaEngagement: { long: "Engagement rate (%)", short: "ER %" },
  reproducciones: { long: "Streams", short: "Streams" },
  espectadores: { long: "Listeners", short: "Listeners" },
  retencionPromedio: { long: "Retención promedio (%)", short: "Retención %" },
  /** YouTube Analytics / Reporting OAuth — thumbnail reach */
  impresionesMiniatura: { long: "Impresiones", short: "Imp. min." },
  ctrMiniatura: { long: "CTR miniatura", short: "CTR min." },
  espectadoresEstimados: {
    long: "Espectadores estimados (on-demand / VOD)",
    short: "Esp. VOD",
  },
} as const;

export type MetricKey = keyof typeof METRICS_LABELS;

/** Get metric label by key and variant */
export const getMetricLabel = (
  key: MetricKey,
  variant: "long" | "short",
): string => METRICS_LABELS[key][variant];

/** Long labels only - for backward compatibility */
export const METRICS_LABELS_LONG = Object.fromEntries(
  Object.entries(METRICS_LABELS).map(([k, v]) => [k, v.long]),
) as { [K in MetricKey]: string };

/** Short labels only - for backward compatibility */
export const METRICS_LABELS_SHORT = Object.fromEntries(
  Object.entries(METRICS_LABELS).map(([k, v]) => [k, v.short]),
) as { [K in MetricKey]: string };

/** For inline content display (e.g. "Views: 1,234") - uses long form */
export const METRICS_LABELS_INLINE = {
  vistas: METRICS_LABELS.vistas.long,
  alcance: METRICS_LABELS.alcance.long,
  likes: METRICS_LABELS.likes.long,
  comentarios: METRICS_LABELS.comentarios.long,
  shares: METRICS_LABELS.shares.long,
  tasaEngagement: "Engagement rate",
  reproducciones: METRICS_LABELS.reproducciones.long,
  espectadores: METRICS_LABELS.espectadores.long,
} as const;

/** For summary/prose text (e.g. "X de visualizaciones") */
export const METRICS_LABELS_SUMMARY = {
  vistas: "visualizaciones",
  reproducciones: "reproducciones",
} as const;

/**
 * Tooltip/hover explanations for each metric.
 */
export const METRICS_TOOLTIPS: Record<MetricKey, string> = {
  cantidad: "Cantidad de piezas de contenido",
  tipoContenido:
    "Categoría de contenido (Videos, Shorts, Reels, Episodios, etc.)",
  vistas: "Número total de veces que se visualizó el contenido",
  espectadoresUnicos:
    "Número de personas únicas que vieron el contenido (YouTube)",
  vistasInvolucradas:
    "Engaged View: vista con interacción en YouTube cuando alguien hace clic para ver el video o sigue reproduciéndolo más allá de los primeros segundos",
  espectadoresEnVivo:
    "Promedio de espectadores en vivo: suma de los espectadores de cada minuto dividida por la cantidad de minutos del episodio",
  espectadoresEnVivoMencion:
    "Espectadores en vivo (mención) — personas viendo en el momento exacto de la mención durante el livestream",
  espectadoresEnVivoPeak:
    "Máximo de espectadores concurrentes en el episodio",
  espectadoresEnVivoPromedio:
    "Promedio de espectadores en vivo: suma de los espectadores de cada minuto dividida por la cantidad de minutos del episodio",
  likes: "Número de usuarios que dieron like al contenido",
  comentarios: "Total de comentarios en el contenido",
  shares: "Veces que el contenido fue compartido por usuarios",
  guardados: "Usuarios que guardaron el contenido (Instagram)",
  alcance: "Cuentas únicas que vieron el contenido",
  interacciones: "Suma de likes, comentarios, shares y guardados",
  tasaEngagement:
    "(Likes + comentarios + shares) / vistas × 100 en YouTube; (likes + comentarios + guardados) / alcance × 100 en Instagram",
  reproducciones:
    "Reproducciones totales del episodio en Spotify (incluye repeticiones)",
  espectadores: "Oyentes únicos del episodio (Spotify)",
  retencionPromedio:
    "Porcentaje promedio del episodio escuchado por la audiencia (Spotify)",
  impresionesMiniatura:
    "Veces que se mostró la miniatura del video (YouTube Analytics / Reporting)",
  ctrMiniatura: "Clics en la miniatura dividido por impresiones (YouTube)",
  espectadoresEstimados:
    "Espectadores estimados (on-demand / VOD) — estimación a partir de retención de audiencia en el momento de la mención",
};

/** Platform-specific tooltip overrides */
export const METRICS_TOOLTIPS_BY_PLATFORM: Partial<
  Record<MetricKey, Record<string, string>>
> = {
  vistas: {
    youtube: "Número de reproducciones del video en YouTube",
    instagram: "Veces que la publicación fue vista en Instagram",
    tiktok: "Número de reproducciones del video en TikTok",
  },
  likes: {
    youtube: "Número de likes en YouTube",
    instagram: "Número de likes en Instagram",
    tiktok: "Número de likes en TikTok",
  },
  comentarios: {
    youtube: "Número de comentarios en YouTube",
    instagram: "Número de comentarios en Instagram",
    tiktok: "Número de comentarios en TikTok",
  },
  shares: {
    youtube: "Veces que el video fue compartido",
    instagram: "Veces que la publicación fue compartida en Instagram",
    tiktok: "Veces que el video fue compartido en TikTok",
  },
  guardados: {
    instagram: "Usuarios que guardaron la publicación en Instagram",
  },
  alcance: {
    instagram: "Cuentas únicas que vieron la publicación en Instagram",
  },
};

/** Get tooltip for a metric, with optional platform override */
export const getMetricTooltip = (key: MetricKey, platform?: string): string => {
  const overrides = METRICS_TOOLTIPS_BY_PLATFORM[key];
  if (platform && overrides?.[platform]) return overrides[platform];
  if (platform === "instagram_private" && overrides?.instagram)
    return overrides.instagram;
  return METRICS_TOOLTIPS[key];
};

/** Tooltip for multi-account shared content (uses metric labels) */
export const getMultiAccountShareTooltip = (): string =>
  `Cuando un reel o publicación se comparte en varias cuentas, el ${getMetricLabel("alcance", "long")} mostrado corresponde solo a las ${getMetricLabel("vistas", "long")} de esta cuenta. Los ${getMetricLabel("likes", "long")}, ${getMetricLabel("comentarios", "long")} y ${getMetricLabel("shares", "long")} pueden estar subrepresentados al no incluir las ${getMetricLabel("interacciones", "long")} de otras cuentas.`;
